import { type PropsWithChildren } from "react";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import FilterCollapse from "@/features/sub-families/components/filter-collapse.tsx";
import SubFamilyFilter from "@/features/sub-families/components/sub-family-filter.tsx";
import useChoicesWithPossibility from "@/features/sub-families/hooks/use-choices-with-possibility.ts";
import type { FilterOption, Properties, PropertyConfigCode } from "@/features/sub-families/types";
import {
    getConfigCodeByProductProperty,
    getEmptyConfigCode,
    getValidSelectedConfigCode,
} from "@/features/sub-families/utils/smart-number.ts";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import Logger from "@/lib/logger.ts";
import { notifyWarning } from "@/lib/notifications.ts";
import { useAppSelector } from "@/redux/store.ts";
import { type Nullable, type Nullish, type SmartNumberDescriptorKey } from "@/types";

type ChangeValue = { descriptor: SmartNumberDescriptorKey; configCode: PropertyConfigCode; isPossible: boolean };
type Props = PropsWithChildren<{
    productProperty: keyof Properties;
    isDesign?: boolean;
    idx?: number;
    config: FilterOption;
    selectedLabel: Nullish<string>;
    /** @desc Only specify when this needs to be customized */
    selectedConfigCode?: PropertyConfigCode;
    allowToggle?: boolean;
    onChange: (value: ChangeValue) => void;
}>;

export default function FilterOption({
    children,
    productProperty,
    config,
    selectedLabel,
    selectedConfigCode,
    onChange,
    idx = -1,
    isDesign = false,
    allowToggle = false,
}: Props) {
    const t = useTranslations();

    const smartNumberMapping = useAppSelector((state) => state.smartNumber.mapping);
    const smartNumberDescription = useAppSelector((state) => state.smartNumber.description);

    const choices = useChoicesWithPossibility(config.choices, productProperty);

    if (!smartNumberMapping) return null;

    const currentConfigCode =
        selectedConfigCode ??
        getConfigCodeByProductProperty(productProperty, config.descriptor, smartNumberMapping, smartNumberDescription);

    const handleClick = (propertyConfigCode: Nullable<PropertyConfigCode>, isPossible: boolean) => {
        const validConfigCode = getValidSelectedConfigCode(
            propertyConfigCode,
            config.descriptor,
            smartNumberDescription,
        );

        if (!validConfigCode) {
            const warning = `Could not correctly determine config code for ${productProperty} (${config.descriptor})`;
            notifyWarning(warning);
            return;
        }

        const change: ChangeValue = { descriptor: config.descriptor, configCode: validConfigCode, isPossible };

        if (allowToggle && validConfigCode === currentConfigCode) {
            const emptyConfigCode = getEmptyConfigCode(config.descriptor, smartNumberDescription);
            if (!emptyConfigCode) {
                const message = `Could not create empty config code for ${config.descriptor}`;
                Logger.warn(message);
                DiagnosticsStore.collectEventLog("warning", message);

                return;
            }

            change.configCode = emptyConfigCode;
        }

        onChange(change);
    };

    return (
        <FilterCollapse
            productProperty={productProperty}
            title={t(config.label)}
            details={selectedLabel}
            isDesign={isDesign}
            idx={idx}
        >
            <SubFamilyFilter
                filterType={config.filterType}
                choices={choices}
                configCode={currentConfigCode}
                onClick={handleClick}
            />
            {children}
        </FilterCollapse>
    );
}
